"use client";

import { useEffect, useRef, memo } from "react";
import type { AiProfile } from "@/types/ai";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { RotateCw } from "lucide-react";
import PolaroidImage from "./PolaroidImage";
import { normaliseAssistantText } from "@/lib/chat/normaliseAssistantText";

interface Props {
  message: {
    id: string;
    role: "user" | "assistant" | "system";
    content: string;
    timestamp?: number;
    images?: Array<{ url: string; prompt?: string }>;
    videos?: Array<{ url?: string; prompt?: string; operationName?: string; status?: string }>;
  };
  profile: AiProfile;
  isStreaming?: boolean;
  previousMessage?: { role: string; timestamp?: number } | null;
  isError?: boolean;
  onRetry?: () => void;
}

function ChatMessage({ message, profile, isStreaming = false, previousMessage, isError, onRetry }: Props) {
  const messageRef = useRef<HTMLDivElement>(null);
  
  // Determine if this message should be grouped with previous
  const isGrouped = previousMessage && 
    previousMessage.role === message.role &&
    previousMessage.timestamp &&
    message.timestamp &&
    (message.timestamp - previousMessage.timestamp) < 120000; // 2 minutes
  
  // Spacing based on grouping
  const spacing = isGrouped ? "mt-2" : "mt-6";
  
  const isUser = message.role === "user";
  const primaryColor = profile.brand?.primaryColor || "#6366f1";
  const secondaryColor = profile.brand?.secondaryColor || "#8b5cf6";
  const textColor = profile.brand?.textColor || "#ffffff";
  
  // Bubble config
  const bubbleConfig = profile.brand?.bubbleConfig || {};
  const userBubbleRadius = bubbleConfig.userBubbleRadius || "1.25rem";
  const assistantBubbleRadius = bubbleConfig.assistantBubbleRadius || "1.25rem";
  const bubblePadding = bubbleConfig.bubblePadding || "1.25rem";
  
  return (
    <div
      ref={messageRef}
      className={`flex ${isUser ? "justify-end" : "justify-start"} ${spacing} animate-in fade-in slide-in-from-bottom-2 duration-300`}
    >
      <div
        className={`max-w-[85%] md:max-w-[75%] lg:max-w-[65%] ${
          isUser ? "order-2" : "order-1"
        }`}
        style={{
          borderRadius: isUser ? userBubbleRadius : assistantBubbleRadius,
          padding: bubblePadding,
          ...(isUser
            ? {
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                color: textColor,
                boxShadow: `0 4px 16px ${primaryColor}30, 0 2px 8px rgba(0,0,0,0.1)`,
              }
            : {
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                color: "#1a1a1a",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
                border: "1px solid rgba(255,255,255,0.5)",
              }),
        }}
      >
        {isUser ? (
          <div
            className="prose prose-sm md:prose-base max-w-none"
            style={{
              color: textColor,
              fontSize: "0.9375rem",
              lineHeight: "1.7",
              letterSpacing: "0.01em",
            }}
          >
            <p className="m-0 whitespace-pre-wrap break-words">{message.content}</p>
          </div>
        ) : (
          <>
            {/* Structured mode: show reasoning steps (before ---) then answer (after ---) */}
            {(() => {
              const sep = message.content.includes("\n---\n")
                ? "\n---\n"
                : message.content.includes("\n--- ")
                ? "\n--- "
                : null;
              const stepsBlock = sep ? message.content.split(sep)[0]?.trim() : null;
              const answerBlock = sep ? message.content.slice(message.content.indexOf(sep) + sep.length).trim() : message.content;
              const steps = stepsBlock
                ? stepsBlock
                    .split("\n")
                    .map((s) => s.replace(/^\s*\d+[.)]\s*/, "").trim())
                    .filter(Boolean)
                : [];
              return (
                <>
                  {steps.length > 0 && (
                    <div
                      className="mb-3 pb-3 border-b border-gray-200/80"
                      style={{ borderColor: `${primaryColor}30` }}
                    >
                      <p className="text-xs font-medium opacity-70 uppercase tracking-wide mb-2" style={{ color: primaryColor }}>
                        Reasoning steps
                      </p>
                      <ol className="list-decimal list-inside space-y-1 text-sm opacity-90">
                        {steps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                  <div
                    className="prose prose-sm md:prose-base max-w-none prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-p:leading-relaxed prose-ul:my-2 prose-ol:my-2 prose-li:my-1 prose-code:text-sm prose-code:bg-black/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-black/5 prose-pre:p-3 prose-pre:rounded-lg prose-pre:overflow-x-auto prose-strong:font-semibold prose-a:text-blue-600 prose-a:underline prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic"
                    style={{
                      color: "#1a1a1a",
                      fontSize: "0.9375rem",
                      lineHeight: "1.75",
                      letterSpacing: "0.01em",
                    }}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => (
                    <p className="m-0 mb-2.5 last:mb-0 leading-[1.6] text-[0.9375rem]" style={{ letterSpacing: '0.01em' }}>{children}</p>
                  ),
                  h1: ({ children }) => (
                    <p className="text-xs font-medium opacity-70 uppercase tracking-wide mt-3 mb-1.5 first:mt-0">{children}</p>
                  ),
                  h2: ({ children }) => (
                    <p className="text-xs font-medium opacity-70 uppercase tracking-wide mt-3 mb-1.5 first:mt-0">{children}</p>
                  ),
                  h3: ({ children }) => (
                    <p className="text-sm font-semibold opacity-80 mt-2.5 mb-1.5 first:mt-0">{children}</p>
                  ),
                  h4: ({ children }) => (
                    <p className="text-sm font-semibold opacity-80 mt-2.5 mb-1.5 first:mt-0">{children}</p>
                  ),
                  h5: ({ children }) => (
                    <p className="text-sm font-semibold opacity-80 mt-2.5 mb-1.5 first:mt-0">{children}</p>
                  ),
                  h6: ({ children }) => (
                    <p className="text-sm font-semibold opacity-80 mt-2.5 mb-1.5 first:mt-0">{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul className="my-2 pl-4 space-y-1 list-disc">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="my-2 pl-4 space-y-1 list-decimal">{children}</ol>
                  ),
                  li: ({ children }) => (
                    <li className="leading-[1.6] text-[0.9375rem]" style={{ letterSpacing: '0.01em' }}>{children}</li>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold">{children}</strong>
                  ),
                  code: ({ children, className }) => {
                    const isInline = !className;
                    return isInline ? (
                      <code className="bg-black/10 text-sm px-1.5 py-0.5 rounded font-mono">
                        {children}
                      </code>
                    ) : (
                      <code className="block bg-black/5 text-sm p-3 rounded-lg overflow-x-auto font-mono">
                        {children}
                      </code>
                    );
                  },
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3 text-gray-700">
                      {children}
                    </blockquote>
                  ),
                  a: ({ children, href }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline hover:text-blue-700 transition-colors"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {normaliseAssistantText(answerBlock)}
              </ReactMarkdown>
            </div>
                </>
              );
            })()}
            
            {/* Retry button for error messages */}
            {isError && onRetry && (
              <div className="mt-3 pt-3 border-t border-gray-200/80">
                <button
                  type="button"
                  onClick={onRetry}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
                >
                  <RotateCw className="w-4 h-4" />
                  Try again
                </button>
              </div>
            )}
            {/* Display Images in Polaroid frames */}
            {message.images && message.images.length > 0 && (
              <div className="mt-3 space-y-4">
                {message.images.map((image, idx) => (
                  <div key={idx} className="flex justify-center">
                    <PolaroidImage
                      src={image.url}
                      alt={image.prompt || `Generated image ${idx + 1}`}
                      prompt={image.prompt}
                      className="max-w-full"
                    />
                  </div>
                ))}
              </div>
            )}
            
            {/* Display Videos */}
            {message.videos && message.videos.length > 0 && (
              <div className="mt-3 space-y-2">
                {message.videos.map((video, idx) => (
                  <div key={idx} className="rounded-lg overflow-hidden">
                    {video.url ? (
                      <video
                        src={video.url}
                        controls
                        className="max-w-full h-auto rounded-lg"
                        style={{ maxHeight: "400px" }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : video.operationName ? (
                      <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                          Video is {video.status === 'completed' ? 'ready' : 'processing'}...
                        </p>
                        {video.prompt && (
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">{video.prompt}</p>
                        )}
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          Operation: {video.operationName}
                        </p>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default memo(ChatMessage);
